import { useMutation, useQueryClient } from '@tanstack/react-query';
import fileDownload from 'js-file-download';
import { toast } from '../../../../components/toast/Toast';
import { getCryptoExport, getCryptoExportFileName } from '../crypto.routes';

const EXPORT_STATUS_RESET_MS = 3000;

type CryptoExportVariables = {
  positionDate: string;
  raptorPlatformId?: string;
};

export const cryptoExportMutationKey = (positionDate?: string) =>
  ['crypto-export', positionDate] as const;

export const useCryptoExportMutation = (positionDate?: string) => {
  const queryClient = useQueryClient();
  const mutationKey = cryptoExportMutationKey(positionDate);

  return useMutation({
    mutationKey,
    mutationFn: ({
      positionDate: date,
      raptorPlatformId,
    }: CryptoExportVariables) => getCryptoExport(date, raptorPlatformId),
    onSuccess: (blob, { positionDate: date }) => {
      fileDownload(blob, getCryptoExportFileName(date));
    },
    onError: (error) => {
      toast({
        title: 'Download failed',
        message:
          error instanceof Error
            ? error.message
            : 'Crypto export failed. Please try again.',
        type: 'error',
        duration: 5000,
      });
    },
    onSettled: (_data, _error, variables) => {
      const completedMutation = queryClient
        .getMutationCache()
        .findAll({ mutationKey, exact: true })
        .find((mutation) => mutation.state.variables === variables);

      if (completedMutation) {
        window.setTimeout(() => {
          queryClient.getMutationCache().remove(completedMutation);
        }, EXPORT_STATUS_RESET_MS);
      }
    },
  });
};
